import api from './api';

export interface BlobDownloadResult {
    fileName: string;
    headers: Record<string, any>;
}

export const getDownloadFileName = (
    disposition: string | undefined,
    fallbackName: string
) => {
    if (!disposition) return fallbackName;

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const normalMatch = disposition.match(/filename="?([^";]+)"?/i);
    const encodedName = utf8Match?.[1] || normalMatch?.[1];

    if (!encodedName) return fallbackName;

    try {
        return decodeURIComponent(encodedName.trim());
    } catch {
        return encodedName.trim();
    }
};

const parseBlobError = async (error: any) => {
    const data = error?.response?.data;

    if (data instanceof Blob) {
        const text = await data.text();

        try {
            return {
                ...error,
                response: {
                    ...error.response,
                    data: JSON.parse(text),
                },
            };
        } catch {
            return error;
        }
    }

    return error;
};

const apiHandler = (resource: string) => ({
    fetchAll: () => api.get(`/${resource}`),
    fetchById: (id: number) => api.get(`/${resource}/${id}`),
    add: (data: Object) => api.post(`/${resource}`, data),
    modify: (id: number, data: Object) =>
        api.put(`/${resource}/${id}`, data),
    update: (data: object) =>
        api.put(`/${resource}`, data),
    remove: (id: number) => api.delete(`/${resource}/${id}`),
    fetchCustom: (customRoute: string) => api.get(`/${resource}${customRoute}`),
    fetchBlob: async (customRoute: string, options?: { silentNotFound?: boolean }) => {
        if (options?.silentNotFound) {
            const response = await api.get(`/${resource}${customRoute}`, { responseType: 'blob' });
            return { data: response.data, headers: response.headers };
        }
        try {
            const response = await api.get(`/${resource}${customRoute}`, {
                responseType: 'blob',
            });

            // 🧠 Compatibilidad total: siempre devuelve response.data como antes
            const result: any = { data: response.data };

            // 🧩 Si existen headers, los anexamos (no rompe nada)
            if (response.headers) {
                result.headers = response.headers;

                // 🔍 Extraer filename si viene en Content-Disposition
                const disposition = response.headers['content-disposition'];
                if (disposition && disposition.includes('filename=')) {
                    const match = disposition.match(/filename="?([^"]+)"?/);
                    if (match?.[1]) {
                        result.fileName = decodeURIComponent(match[1]);
                    }
                }
            }

            return result;
        } catch (error) {
            console.error(`❌ Error al obtener blob de ${resource}:`, error);
            throw error;
        }
    },
    addCustom: (customRoute: string, data: Object) =>
        api.post(`/${resource}${customRoute}`, data),
    addCustomWithHeaders: (
        customRoute: string,
        data: Object,
        headers: Record<string, string>
    ) => api.post(`/${resource}${customRoute}`, data, { headers }),
    addPathVariable: (path: string, data: Object = {}) =>
        api.post(`/${resource}/${path}`, data),
    addWithoutData: (customRoute: string) => api.post(`/${resource}${customRoute}`),
    modifyCustom: (customRoute: string, data: Object) =>
        api.put(`/${resource}${customRoute}`, data),
    modifyPathVariable: (path: string) =>
        api.put(`/${resource}/${path}`),
    removeCustom: (customRoute: string) =>
        api.delete(`/${resource}${customRoute}`),
    upload: (data: FormData) =>
        api.post(`/${resource}`, data, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),
    updateUpload: (id: number, data: FormData) =>
        api.put(`/${resource}/${id}`, data, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),
    uploadCustom: (customRoute: string, data: FormData) =>
        api.post(`/${resource}${customRoute}`, data, {
            headers: { "Content-Type": "multipart/form-data" },
        }),

    // Nuevo método genérico para `PATCH`
    patch: (id: number) =>
        api.patch(`/${resource}/${id}`),

    patchCustom: (customRoute: string, data: Object = {}) =>
        api.patch(`/${resource}/${customRoute}`, data),

    // ✅ POST con soporte para uno o dos archivos
    postFormData: (
        data: Record<string, any>,
        file?: File,     // archivo principal
        file2?: File     // archivo secundario genérico
    ) => {
        const formData = new FormData();

        // Agregar los datos planos
        Object.keys(data).forEach((key) => {
            if (data[key] !== undefined && data[key] !== null) {
                formData.append(key, data[key]);
            }
        });

        // Agregar archivos
        if (file) {
            formData.append('file', file);    // archivo principal
        }

        if (file2) {
            formData.append('file2', file2);  // segundo archivo genérico
        }

        return api.post(`/${resource}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    postFormDataCustom: (
        customRoute: string,
        data: Record<string, any>,
        file?: File,
        fileFieldName = "file"
    ) => {
        const formData = new FormData();

        // Agregar campos normales al FormData
        Object.keys(data).forEach((key) => {
            if (data[key] !== undefined && data[key] !== null) {
                formData.append(key, data[key]);
            }
        });

        // Agregar archivo
        if (file) {
            formData.append(fileFieldName, file);
        }

        // 🔁 Usamos la ruta personalizada
        return api.post(`/${resource}${customRoute}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    // ✅ PUT con soporte para uno o dos archivos
    putFormData: (
        id: number,
        data: Record<string, any>,
        file?: File,     // archivo principal
        file2?: File     // archivo secundario genérico
    ) => {
        const formData = new FormData();

        // Agregar los datos planos
        Object.keys(data).forEach((key) => {
            if (data[key] !== undefined && data[key] !== null) {
                formData.append(key, data[key]);
            }
        });

        // Agregar archivos
        if (file) {
            formData.append('file', file);    // archivo principal
        }

        if (file2) {
            formData.append('file2', file2);  // segundo archivo genérico
        }

        return api.put(`/${resource}/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    putFormDataCustom: (
        customRoute: string,
        data: Record<string, any>,
        file?: File,
        fileFieldName = "file"
    ) => {
        const formData = new FormData();

        // Agregar datos al FormData
        Object.keys(data).forEach((key) => {
            if (data[key] !== undefined && data[key] !== null) {
                formData.append(key, data[key]);
            }
        });

        // Agregar archivo si existe
        if (file) {
            formData.append(fileFieldName, file);
        }

        return api.put(`/${resource}${customRoute}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    putFormDataByPath: (
        path: string,                         // 🔸 path dinámico en vez de un ID
        data: Record<string, any> = {},
        file?: File,
        file2?: File
    ) => {
        const formData = new FormData();

        // Agregar los datos planos
        Object.keys(data).forEach((key) => {
            if (data[key] !== undefined && data[key] !== null) {
                formData.append(key, data[key]);
            }
        });

        // Agregar archivos
        if (file) {
            formData.append('file', file);
        }

        if (file2) {
            formData.append('file2', file2);
        }

        return api.put(`/${resource}/${path}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    downloadFileFromBlob: async (
        customRoute: string,
        requestData?: Object,
        defaultFileName?: string
    ): Promise<string> => {
        try {
            const response = requestData
                ? await api.post(`/${resource}${customRoute}`, requestData, {
                    responseType: 'blob',
                })
                : await api.get(`/${resource}${customRoute}`, {
                    responseType: 'blob',
                });

            const contentType = String(
                response.headers['content-type'] || 'application/octet-stream'
            );

            let fileName =
                defaultFileName ||
                (contentType.includes('pdf') ? 'reporte.pdf' : 'reporte.xlsx');

            const disposition = response.headers['content-disposition']
                ? String(response.headers['content-disposition'])
                : undefined;

            if (disposition) {
                const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/);

                if (utf8Match?.[1]) {
                    fileName = decodeURIComponent(utf8Match[1]);
                } else {
                    const normalMatch = disposition.match(
                        /filename="?([^";]+)"?/
                    );

                    if (normalMatch?.[1]) {
                        fileName = decodeURIComponent(normalMatch[1]);
                    }
                }
            }

            if (contentType.includes('application/json')) {
                const text = await new Response(response.data).text();
                const errorJson = JSON.parse(text);

                const error: any = new Error(
                    errorJson.message || 'Error en la exportación'
                );

                error.response = {
                    data: errorJson,
                    headers: response.headers,
                    status: response.status,
                };

                throw error;
            }

            const blob = new Blob([response.data], {
                type: contentType,
            });

            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();

            a.remove();
            window.URL.revokeObjectURL(url);

            return fileName;
        } catch (error: any) {
            if (error?.response?.data instanceof Blob) {
                try {
                    const text = await new Response(error.response.data).text();
                    const json = JSON.parse(text);
                    error.response.data = json;
                } catch {
                    console.warn('No se pudo parsear el blob de error como JSON');
                }
            }

            throw error;
        }
    },

    downloadBlobGet: async (
        customRoute: string,
        fallbackName = 'archivo.bin',
        onProgress?: (progress: number | null) => void,
        allowJsonWithoutDisposition = false
    ): Promise<BlobDownloadResult> => {
        try {
            onProgress?.(0);

            const response = await api.get(
                `/${resource}${customRoute}`,
                {
                    responseType: 'blob',

                    headers: {
                        Accept:
                            'application/octet-stream, application/zip, application/pdf, application/json',
                    },

                    onDownloadProgress: (progressEvent) => {
                        if (!onProgress) {
                            return;
                        }

                        const loaded = progressEvent.loaded;
                        const total = progressEvent.total;

                        if (!total || total <= 0) {
                            onProgress(null);
                            return;
                        }

                        const progress = Math.min(
                            Math.round((loaded / total) * 100),
                            100
                        );

                        onProgress(progress);
                    },
                }
            );

            const contentType = String(
                response.headers['content-type']
                || 'application/octet-stream'
            );

            const disposition =
                response.headers['content-disposition']
                    ? String(
                        response.headers['content-disposition']
                    )
                    : undefined;

            if (
                contentType.includes('application/json')
                && disposition === undefined
                && !allowJsonWithoutDisposition
            ) {
                const text = await response.data.text();

                const data = JSON.parse(text);

                const error: any = new Error(
                    data.message || 'Error en la descarga'
                );

                error.response = {
                    data,
                    headers: response.headers,
                    status: response.status,
                };

                throw error;
            }

            const fileName = getDownloadFileName(
                disposition,
                fallbackName
            );

            const blob =
                response.data instanceof Blob
                    ? response.data
                    : new Blob(
                        [response.data],
                        {
                            type: contentType,
                        }
                    );

            onProgress?.(100);

            const url = window.URL.createObjectURL(blob);

            const anchor = document.createElement('a');

            anchor.href = url;
            anchor.download = fileName;
            anchor.style.display = 'none';

            document.body.appendChild(anchor);

            anchor.click();

            setTimeout(() => {
                anchor.remove();

                window.URL.revokeObjectURL(url);
            }, 1000);

            return {
                fileName,
                headers:
                    response.headers as Record<string, any>,
            };

        } catch (error: any) {
            throw await parseBlobError(error);
        }
    },

    downloadBlob: async (
        customRoute: string,
        requestData: Object,
        fallbackName = 'archivo.bin'
    ): Promise<BlobDownloadResult> => {
        try {
            const response = await api.post(
                `/${resource}${customRoute}`,
                requestData,
                {
                    responseType: 'blob',
                    headers: { Accept: 'application/zip, application/json' },
                }
            );
            const contentType = String(
                response.headers['content-type'] || 'application/octet-stream'
            );

            if (contentType.includes('application/json')) {
                const data = JSON.parse(await response.data.text());
                const error: any = new Error(data.message || 'Error en la descarga');
                error.response = { data, headers: response.headers, status: response.status };
                throw error;
            }

            const fileName = getDownloadFileName(
                response.headers['content-disposition']
                    ? String(response.headers['content-disposition'])
                    : undefined,
                fallbackName
            );
            const url = window.URL.createObjectURL(response.data);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);

            return { fileName, headers: response.headers as Record<string, any> };
        } catch (error: any) {
            throw await parseBlobError(error);
        }
    },
    downloadJsonFile: async (customRoute: string): Promise<string> => {
        try {
            const response = await api.get(`/${resource}${customRoute}`, {
                responseType: 'blob',
            });

            const contentType = response.headers['content-type']
                ? String(response.headers['content-type'])
                : undefined;

            // 🟢 Nombre del archivo desde el header
            let fileName = 'archivo.json';
            const disposition = response.headers['content-disposition']
                ? String(response.headers['content-disposition'])
                : undefined;
            if (disposition && disposition.includes('filename=')) {
                const match = disposition.match(/filename="?([^"]+)"?/);
                if (match?.[1]) {
                    fileName = decodeURIComponent(match[1]);
                }
            }

            // 🟥 Detectar si el backend respondió con error en lugar de blob
            if (contentType?.includes('application/json') && disposition === undefined) {
                const text = await new Response(response.data).text();
                const errorJson = JSON.parse(text);
                const error: any = new Error(errorJson.message || 'Error en la descarga del JSON');
                error.response = {
                    data: errorJson,
                    headers: response.headers,
                    status: response.status,
                };
                throw error;
            }

            // ✅ Crear blob y descargar como .json
            const blob = new Blob([response.data], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);

            return fileName;
        } catch (error: any) {
            if (error?.response?.data instanceof Blob) {
                try {
                    const text = await new Response(error.response.data).text();
                    const json = JSON.parse(text);
                    error.response.data = json;
                } catch (e) {
                    console.warn('No se pudo parsear el blob de error como JSON');
                }
            }
            throw error;
        }
    }


});

export default apiHandler;
